import type { ReactElement } from 'react';
import {
  PageErrorState,
  PageForbiddenState,
  PageLoadingState,
  PageMaintenanceState,
  PageNotFoundState,
  PagePlannedState,
  PageUnavailableState,
} from '../../components/states';
import type { RouteAccessResult, RouteAccessStatus } from './use-route-access';

export function isReadableRouteAccess(status: RouteAccessStatus): boolean {
  return status === 'AVAILABLE' || status === 'READ_ONLY';
}

export function isWriteableRouteAccess(status: RouteAccessStatus): boolean {
  return status === 'AVAILABLE';
}

export interface RouteAccessStateViewProps {
  access: RouteAccessResult;
  routeId?: string;
}

/**
 * Renders the canonical user-facing state for a backend route decision.
 * Returns null only when the route is actionable, allowing callers to render
 * their normal surface without reimplementing route semantics.
 */
export function RouteAccessStateView({ access, routeId }: RouteAccessStateViewProps): ReactElement | null {
  const { status, reason, remediation, decisionId } = access;
  const stateProps = {
    routeId: routeId ?? '',
    disabledReasonKey: reason,
    remediation,
    decisionId,
  };

  switch (status) {
    case 'LOADING':
      return <PageLoadingState />;
    case 'NOT_FOUND':
    case 'HIDDEN':
      return <PageNotFoundState />;
    case 'LOCKED':
    case 'RESTRICTED':
      return (
        <PageForbiddenState
          remediation={remediation}
          decisionId={decisionId}
          reasonCode={reason}
        />
      );
    case 'PLANNED':
      return <PagePlannedState {...stateProps} />;
    case 'MAINTENANCE':
      return <PageMaintenanceState {...stateProps} />;
    case 'UNAVAILABLE':
      return <PageUnavailableState {...stateProps} />;
    case 'SERVICE_ERROR':
      return <PageErrorState reasonCode={reason} />;
    case 'AVAILABLE':
    case 'READ_ONLY':
      return null;
    default:
      return <PageNotFoundState />;
  }
}
